import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { usePermissions } from "../../../server/auth/use-permissions";
import type { MomentFormMoment } from "../types/moment-form-moment";

type FormPart =
  | "basic-form"
  | "channel"
  | "segment"
  | "schedule"
  | "message"
  | "publish-button"
  | "draft-button"
  | "sent-test-button"
  | "pause-edit-button"
  | "preview-button";

export interface FormSaveState {
  saving: boolean;
  setSaving: (saving: boolean) => void;
  publishing: boolean;
  setPublishing: (publishing: boolean) => void;
  lastAction?: "draft" | "publish";
  setLastAction: (action: "draft" | "publish") => void;
  formDisabled: {
    [key in FormPart]: boolean;
  };
}

export function useFormSaveState(userReadOnlyRole: boolean = false): FormSaveState {
  const { control, getValues } = useFormContext<MomentFormMoment>();

  const { ce_admin } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [lastAction, setLastAction] = useState<"draft" | "publish" | undefined>();

  useWatch({ control, name: ["running", "sent", "status", "immediate"] });
  const [running, sent, status, immediate] = getValues(["running", "sent", "status", "immediate"]);

  const runningOrSent = running || sent;

  const archived = status === "archived" || status === "finished";

  return {
    saving,
    setSaving,
    publishing,
    setPublishing,
    lastAction,
    setLastAction,
    formDisabled: {
      "basic-form": running || archived || userReadOnlyRole,
      channel: runningOrSent || archived || userReadOnlyRole,
      segment: archived || (ce_admin ? running : runningOrSent) || userReadOnlyRole,
      schedule: archived || running || userReadOnlyRole,
      message: archived || running || userReadOnlyRole,
      "publish-button": running || archived || userReadOnlyRole,
      "draft-button": running || archived || userReadOnlyRole,
      "sent-test-button": running || archived || userReadOnlyRole,
      "pause-edit-button":
        archived || (immediate && (running || sent)) || !running || userReadOnlyRole,
      "preview-button": status === "archived" || userReadOnlyRole,
    },
  };
}
